import React, { useState, useEffect, useRef } from "react";
import {
  LightModeOutlined,
  DarkModeOutlined,
  ArrowDropDownOutlined,
  Star as StarIcon,
  Close as CloseIcon,
  NotificationsOutlined,
  HelpOutlineOutlined,
  SettingsOutlined,
  VisibilityOutlined,
  Menu as MenuIcon,
} from "@mui/icons-material";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  IconButton,
  Drawer,
  useTheme,
  Badge,
  Avatar,
  MenuItem,
  Divider,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setMode } from "state";
import { logout } from "state/authSlice";
import { useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import ScoreDashboard from "./ScoreDashboard";
import { useGetBestScorersQuery, useUpdatepasswordMutation } from "state/api";

const Navbar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { username, MgCd, EmpNo } = useSelector((state) => state.global);
  const [notifications] = useState([]);

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isStarSidebarOpen, setIsStarSidebarOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const profileMenuRef = useRef(null);
  const passwordResetRef = useRef(null);

  const [updatePassword] = useUpdatepasswordMutation();
  const { data: bestScorers } = useGetBestScorersQuery({
    product: selectedProduct,
  });

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target) &&
        (!passwordResetRef.current ||
          !passwordResetRef.current.contains(event.target))
      ) {
        setShowProfileMenu(false);
        setIsUpdatingPassword(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Password reset state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  const handlePasswordReset = async () => {
    setPasswordError("");
    if (!newPassword || !confirmPassword) {
      return setPasswordError("Please enter both new password fields.");
    }
    if (newPassword !== confirmPassword) {
      return setPasswordError("Passwords do not match.");
    }
    try {
      const res = await updatePassword({
        EmpNo,
        newpassword: newPassword,
      }).unwrap();
      alert(res.message || "Password updated successfully.");
      setNewPassword("");
      setConfirmPassword("");
      setIsUpdatingPassword(false);
    } catch (err) {
      alert("Failed to update password.");
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/LoginPage");
  };

  const toggleStarSidebar = () => {
    setIsStarSidebarOpen((prev) => !prev);
    if (showProfileMenu) setShowProfileMenu(false);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu((prev) => !prev);
    if (isStarSidebarOpen) setIsStarSidebarOpen(false);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    if (showProfileMenu) setShowProfileMenu(false);
    if (isStarSidebarOpen) setIsStarSidebarOpen(false);
  };

  // Mobile menu content
  const mobileMenuContent = (
    <Box sx={{ p: 2 }}>
      <Button
        fullWidth
        onClick={toggleStarSidebar}
        startIcon={<StarIcon />}
        sx={{
          justifyContent: "flex-start",
          mb: 1,
          color: "#e2e8f0",
          "&:hover": {
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            color: "#3b82f6",
          },
        }}
      >
        Top Performers
      </Button>
      <Divider sx={{ my: 1, borderColor: "rgba(59, 130, 246, 0.2)" }} />
      <Button
        fullWidth
        startIcon={<HelpOutlineOutlined />}
        sx={{
          justifyContent: "flex-start",
          mb: 1,
          color: "#e2e8f0",
          "&:hover": {
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            color: "#3b82f6",
          },
        }}
      >
        Help Center
      </Button>
      <Button
        fullWidth
        startIcon={<NotificationsOutlined />}
        sx={{
          justifyContent: "flex-start",
          mb: 1,
          color: "#e2e8f0",
          "&:hover": {
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            color: "#3b82f6",
          },
        }}
      >
        Notifications
      </Button>
      <Button
        fullWidth
        startIcon={<SettingsOutlined />}
        sx={{
          justifyContent: "flex-start",
          mb: 1,
          color: "#e2e8f0",
          "&:hover": {
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            color: "#3b82f6",
          },
        }}
      >
        Settings
      </Button>
      <Button
        fullWidth
        onClick={() => dispatch(setMode())}
        startIcon={
          theme.palette.mode === "dark" ? (
            <DarkModeOutlined />
          ) : (
            <LightModeOutlined />
          )
        }
        sx={{
          justifyContent: "flex-start",
          mb: 1,
          color: "#e2e8f0",
          "&:hover": {
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            color: "#3b82f6",
          },
        }}
      >
        {theme.palette.mode === "dark" ? "Dark Mode" : "Light Mode"}
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          background: "linear-gradient(90deg, #0f172a 0%, #1e293b 100%)",
          boxShadow: "0px 4px 12px rgba(59, 130, 246, 0.15)",
          borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
          zIndex: 1300,
          backdropFilter: "blur(8px)",
        }}
      >
        <Toolbar sx={{ padding: isMobile ? "8px 16px" : "8px 24px" }}>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              onClick={toggleMobileMenu}
              sx={{ mr: 1, color: theme.palette.text.primary }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Star Performer Button - shown on all screens */}
          <Button
            onClick={toggleStarSidebar}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              textTransform: "none",
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              color: "white",
              "&:hover": {
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                boxShadow: "0 4px 16px rgba(59, 130, 246, 0.4)",
              },
              borderRadius: "10px",
              padding: "8px 16px",
              transition: "all 0.3s ease",
              mr: isMobile ? "auto" : 2,
              fontWeight: 600,
            }}
          >
            <StarIcon sx={{ color: "#fbbf24", fontSize: "20px" }} />
            <span>{isMobile ? "Top" : "Top Performers"}</span>
          </Button>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}
            >
              <IconButton
                sx={{
                  color: "#94a3b8",
                  "&:hover": {
                    backgroundColor: "rgba(59, 130, 246, 0.15)",
                    color: "#3b82f6",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <HelpOutlineOutlined />
              </IconButton>

              <IconButton
                sx={{
                  color: "#94a3b8",
                  "&:hover": {
                    backgroundColor: "rgba(59, 130, 246, 0.15)",
                    color: "#3b82f6",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <Badge badgeContent={notifications.length} color="error">
                  <NotificationsOutlined />
                </Badge>
              </IconButton>

              <IconButton
                sx={{
                  color: "#94a3b8",
                  "&:hover": {
                    backgroundColor: "rgba(59, 130, 246, 0.15)",
                    color: "#3b82f6",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <SettingsOutlined />
              </IconButton>

              <IconButton
                onClick={() => dispatch(setMode())}
                sx={{
                  color: "#94a3b8",
                  "&:hover": {
                    backgroundColor: "rgba(59, 130, 246, 0.15)",
                    color: "#3b82f6",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {theme.palette.mode === "dark" ? (
                  <DarkModeOutlined />
                ) : (
                  <LightModeOutlined />
                )}
              </IconButton>
            </Box>
          )}

          {/* Profile Section - shown on all screens */}
          <Box sx={{ position: "relative", ml: isMobile ? 1 : 0 }}>
            <Button
              variant="text"
              onClick={toggleProfileMenu}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textTransform: "none",
                color: "#e2e8f0",
                "&:hover": {
                  backgroundColor: "rgba(59, 130, 246, 0.15)",
                },
                borderRadius: "10px",
                padding: "6px 12px",
                minWidth: "auto",
                transition: "all 0.3s ease",
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "white",
                }}
              >
                {username.charAt(0).toUpperCase()}
              </Avatar>
              {!isMobile && (
                <>
                  <Box textAlign="left">
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      noWrap
                      sx={{ color: "#e2e8f0" }}
                    >
                      {username.trim().split(" ")[0] || "User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{ color: "#94a3b8" }}
                    >
                      {MgCd || "Role"}
                    </Typography>
                  </Box>
                  <ArrowDropDownOutlined
                    fontSize="small"
                    sx={{ color: "#94a3b8" }}
                  />
                </>
              )}
            </Button>

            {/* Profile Menu Dropdown */}
            {showProfileMenu && (
              <Box
                ref={profileMenuRef}
                sx={{
                  position: "absolute",
                  top: "56px",
                  right: 0,
                  backgroundColor: "#1e293b",
                  boxShadow: "0 8px 32px rgba(59, 130, 246, 0.25)",
                  borderRadius: "12px",
                  padding: "8px 0",
                  zIndex: 9999,
                  width: isMobile ? "100vw" : 280,
                  maxWidth: "calc(100vw - 32px)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  overflow: "hidden",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    noWrap
                    sx={{ color: "#e2e8f0" }}
                  >
                    {username}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ color: "#94a3b8" }}>
                    {EmpNo || "Employee ID"}
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: "rgba(59, 130, 246, 0.2)" }} />

                <Box sx={{ p: 2 }} ref={passwordResetRef}>
                  {!isUpdatingPassword ? (
                    <>
                      <MenuItem
                        onClick={() => setIsUpdatingPassword(true)}
                        sx={{
                          borderRadius: "8px",
                          color: "#e2e8f0",
                          "&:hover": {
                            backgroundColor: "rgba(59, 130, 246, 0.15)",
                            color: "#3b82f6",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Typography variant="body2">Reset Password</Typography>
                      </MenuItem>
                      <MenuItem
                        onClick={handleLogout}
                        sx={{
                          borderRadius: "8px",
                          color: "#ef4444",
                          "&:hover": {
                            backgroundColor: "rgba(239, 68, 68, 0.15)",
                            color: "#fca5a5",
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <Typography variant="body2">Logout</Typography>
                      </MenuItem>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                        sx={{ color: "#e2e8f0" }}
                      >
                        Reset Password
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            color: "#e2e8f0",
                            backgroundColor: "rgba(15, 23, 42, 0.5)",
                            "& fieldset": {
                              borderColor: "rgba(59, 130, 246, 0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3b82f6",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                            },
                          },
                          "& .MuiOutlinedInput-input::placeholder": {
                            color: "#64748b",
                            opacity: 1,
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                size="small"
                                sx={{ color: "#94a3b8" }}
                              >
                                <VisibilityOutlined fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            color: "#e2e8f0",
                            backgroundColor: "rgba(15, 23, 42, 0.5)",
                            "& fieldset": {
                              borderColor: "rgba(59, 130, 246, 0.3)",
                            },
                            "&:hover fieldset": {
                              borderColor: "#3b82f6",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#3b82f6",
                            },
                          },
                          "& .MuiOutlinedInput-input::placeholder": {
                            color: "#64748b",
                            opacity: 1,
                          },
                        }}
                      />
                      {passwordError && (
                        <Typography color="error" variant="caption">
                          {passwordError}
                        </Typography>
                      )}
                      <Box display="flex" gap={1} mt={1}>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => setIsUpdatingPassword(false)}
                          size="small"
                          sx={{
                            borderColor: "rgba(59, 130, 246, 0.5)",
                            color: "#3b82f6",
                            "&:hover": {
                              borderColor: "#3b82f6",
                              backgroundColor: "rgba(59, 130, 246, 0.1)",
                            },
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={handlePasswordReset}
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                            },
                          }}
                        >
                          Update
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="left"
        open={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: "#0f172a",
            borderRight: "1px solid rgba(59, 130, 246, 0.2)",
          },
        }}
      >
        {mobileMenuContent}
      </Drawer>

      {/* Star Performers Drawer */}
      <Drawer
        anchor="left"
        open={isStarSidebarOpen}
        onClose={toggleStarSidebar}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 320 },
            backgroundColor: "#0f172a",
            borderRight: "1px solid rgba(59, 130, 246, 0.2)",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#0f172a",
          }}
        >
          <FlexBetween
            sx={{ p: 2, borderBottom: "1px solid rgba(59, 130, 246, 0.2)" }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ color: "#e2e8f0" }}>
              ⭐ Top Performers
            </Typography>
            <IconButton
              onClick={toggleStarSidebar}
              size="small"
              sx={{ color: "#94a3b8" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </FlexBetween>

          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <ScoreDashboard
              product={selectedProduct}
              setSelectedProduct={setSelectedProduct}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
